<template>
  <div v-loading="loading || proReportDataLoading" element-loading-spinner="atom-audit-loader" style="min-height: 200px;">
    <div v-for="group in allData" :key="group.key">
      <template v-if="!codeType || group.key === codeType">
        <h2>{{ group.key }}</h2>
        <div style="position: relative; float: right; margin: 0px 0px -10px 0px;">
          <el-dropdown trigger="click" @command="handleTableCommands">
            <span class="el-dropdown-link">
              <i title="table actions" class="mdi mdi-dots-horizontal" style="font-size: 20px; cursor: pointer;"></i>
            </span>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item :command="buildTableCommands('download', `hccTable_${group.key}`)">Download</el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </div>
        <el-table :id="`hccTable_${group.key}`" class="tableNoWrap" :ref="`hccTable_${group.key}`" :data="group.data" empty-text="No HCC Data Available" max-height="425"
          stripe :summary-method="getSummaries" :cell-class-name="cellClasses" show-summary row-key="code">
          <el-table-column prop="code" label="HCC">
            <template slot-scope="scope">
              <div tag="hcc">
                <span v-for="(item, index) in convertHccs(scope.row.code)" v-bind:key="item">
                  <span v-html="highlightValue(item, filterCriteria)"></span>
                  <span v-if="index + 1 !== convertHccs(scope.row.code).length">&bull;</span>
                </span>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="description" label="Description" sortable>
            <template slot-scope="scope">
              <div tag="description" :title="scope.row.description" v-html="highlightValue(scope.row.description, filterCriteria)"></div>
            </template>
          </el-table-column>
          <el-table-column prop="numOfNoRec" label="No Recommendations" sortable>
            <template slot-scope="scope">
              <div v-html="highlightValue(scope.row.numOfNoRec, filterCriteria)" title="No Recommendations"></div>
            </template>
          </el-table-column>
          <el-table-column prop="numOfAdditions" label="Additions" sortable>
            <template slot-scope="scope">
              <div v-html="highlightValue(scope.row.numOfAdditions, filterCriteria)" title="Additions"></div>
            </template>
          </el-table-column>
          <el-table-column prop="numOfDeletions" label="Deletions" sortable>
            <template slot-scope="scope">
              <div v-html="highlightValue(scope.row.numOfDeletions, filterCriteria)" title="Deletions" class="details"></div>
            </template>
          </el-table-column>
          <el-table-column prop="hccCodes" label="Accuracy Rate" sortable>
            <template slot-scope="scope">
              <div v-html="highlightValue(`${(scope.row.accuracyRate * 100).toFixed(2)}%`, filterCriteria)" :class="`hcc_hccCodes_${scope.$index} details`"></div>
            </template>
          </el-table-column>
          <el-table-column label="Details" width="120" prop="details" v-if="!hideDetails">
            <template slot-scope="scope">
              <el-button class="dx-button" @click.native.prevent="updateAcctListDx(scope.row)" plain round size="mini" icon="mdi mdi-view-dashboard-variant-outline"
                :title="`View Diagnosis Codes for HCC ${scope.row.description}`" circle>
              </el-button>
              <el-button @click.native.prevent="updateAcctList(scope.row)" plain round size="mini" icon="mdi mdi-details" title="Records Included" circle> </el-button>
            </template>
          </el-table-column>
        </el-table>
      </template>
    </div>

    <center v-if="!loading">
      <div class="no-data-message" v-if="allData.length === 0">No HCC Data Available</div>
    </center>

    <el-dialog :visible.sync="dialogVisible" center destroy-on-close width="75%">
      <accountList :header="detailsHeader" v-if="dialogVisible" :proRecordIds="subProRecordIds"></accountList>
    </el-dialog>

    <el-dialog :title="detailsHeader" :visible.sync="dxDialogVisible" center width="75%">
      <PRODx v-if="dxDialogVisible" class="dashboard-drilldown" :hideDetails="true" :proRecordIds="subProRecordIds" style="min-height: 300px;" :hcc="hcc"></PRODx>
    </el-dialog>

  </div>
</template>

<script>
  import { mapState } from 'vuex'
  import axios from '@/utilities/axios-global'
  import hiaFilters from '@/mixins/hiaFilters'
  import money from '@/mixins/money'
  import dashboardHelper from '@/mixins/dashboardHelper'
  import accountList from './accoutList'
  import PRODx from './PRO_DxSummary.vue'

  export default {
    name: 'PRO_HCCSummary',
    mixins: [hiaFilters, money, dashboardHelper],
    props: ['proRecordIds', 'hideDetails', 'codeType', 'codeTypes'],
    components: {
      accountList,
      PRODx
    },
    data: function () {
      return {
        allData: [],
        filterCriteria: '',
        loading: false,
        dialogVisible: false,
        summaryDialogVisible: false,
        dxDialogVisible: false,
        subProRecordIds: null,
        summaryRow: {},
        detailsHeader: '',
        hcc: {}
      }
    },
    methods: {
      async getData() {
        const payload = {
          proRecordIds: this.proRecordIds,
          codeTypes: this.codeTypes,
          includeAccts: true,
          statType: 'test'
        }
        try {
          this.loading = true
          const response = await axios.post(this.baseApiUrl + '/reports/dashboard/proByHCCV28', payload)
          this.allData = response.data.data
          if (this.codeType === 'All Code Types' && !this.allData.some(x => x.key === this.codeType)) {
            const codes = this.getAllCodeTypesPro(this.allData, 'accuracyRate')
            this.allData = [{ key: 'All Code Types', data: codes }]
          }
          if (this.codeType && !this.allData.some(x => x.key === this.codeType)) {
            this.allData.push({ key: this.codeType, data: [] })
          }
        } catch (err) {
          this.allData = []
        } finally {
          this.loading = false
        }
      },
      updateAcctList(row) {
        this.detailsHeader = row.code
        this.subProRecordIds = row.accountNumbers
        this.dialogVisible = !this.dialogVisible
      },
      updateAcctListDx(row) {
        this.detailsHeader = `Dx Codes for ${row.description}, HCC V28`
        this.subProRecordIds = row.accountNumbers
        this.hcc = {
          hcc: row.code,
          hccVersion: 'HCCV28',
          codeDesc: row.codeDesc
        }
        this.dxDialogVisible = !this.dxDialogVisible
      },
      getSummaries(param) {
        return this.getSummary(param)
      },
      convertHccs(delimited) {
        if (delimited) {
          const retArray = delimited.split('-')
          return retArray
        } else {
          return []
        }
      },
      cellClasses({ row, column, rowIndex, columnIndex }) {
        if (column.property === 'code' || column.property === 'description' || column.property === 'codeDesc') {
          return 'textAlignLeft'
        }
        if (column.property === 'details') {
          return 'textAlignCenter'
        }
      },
      getAllCodeTypesPro(data, accuracyColKey) {
        let arr = []
        data.forEach(x => {
          arr = arr.concat(x.data)
        })
        const allCodeTypes = []
        arr.reduce((res, value) => {
          if (!res[value.code]) {
            res[value.code] = {
              code: value.code,
              hcc: value.hcc,
              description: value.description,
              cmCodes: 0,
              totalRecords: 0,
              dxAdditions: 0,
              modDeletions: 0,
              dxNoRec: 0,
              dxRevisions: 0
            }
            allCodeTypes.push(res[value.code])
          }
          const row = res[value.code]
          row.dxAdditions += value.dxAdditions
          row.modDeletions += value.modDeletions
          row.dxNoRec += value.dxNoRec
          row.dxRevisions += value.dxRevisions
          row.totalRecords += value.totalRecords

          let mt = 1
          let adr = 1

          if (!isNaN(Number(row.dxNoRec)) || !isNaN(Number(row.dxAdditions)) || !isNaN(Number(row.modDeletions)) || !isNaN(Number(row.dxRevisions))) {
            mt = row.dxNoRec + row.dxAdditions + row.modDeletions + row.dxRevisions
          }

          if (!isNaN(Number(row.dxAdditions)) || !isNaN(Number(row.modDeletions)) || !isNaN(Number(row.dxRevisions))) {
            adr = row.dxAdditions + row.modDeletions + row.dxRevisions
          }
          row[accuracyColKey] = mt || adr ? 1 - adr / mt : null
          return res
        }, {})
        return allCodeTypes
      },
      handleTableCommands(command) {
        this.$emit('handleTableCommands', { command: command.command, ref: command.key, csvName: `${command.key}_table`, headers: [], excludeHeaders: ['Details'] })
      },
      buildTableCommands(command, key) {
        const commandObject = {
          command: command,
          key: key
        }
        return commandObject
      }
    },
    computed: {
      ...mapState('user/', ['baseApiUrl']),
      ...mapState('reports/', ['proReportDataLoading'])
    },
    mounted: async function () {
      await this.getData()
    },
    activated: async function () {
      await this.getData()
    },
    watch: {
      proRecordIds: async function () {
        await this.getData()
      }
    }
  }
</script>

<style scoped></style>